import { searchFilterFilterType, searchOperator } from '@ingenico/api-sdk-provisioning-public';
import { useBusinessServiceRequest, useQueryFirstLoading } from '@ingenico/react-section-utils';
import type { GridSortModel } from '@mui/x-data-grid-premium';
import { toArray } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import type { ResponseListData } from '../components/device-app-organizations-tab/DeviceAppOrganizationsTab.model';
import { DeviceManagementService } from '../domain/business/services/deviceManagement.service';
import type {
  SearchOrganizationsV1MutationRequest,
  SearchOrganizationsV1QueryParams,
} from '../domain/models/provisioningOrganizationList.model';
import {
  getAppStoreOrgTabCommonFilters,
  getDisplayedAppStoreOrganizationTabInfo,
  transformOrganizationGridSortToRequestCall,
} from '../utils/appStoreOrgTab.util';

export interface UseOrganizationDetailsTabRequestParams {
  readonly organizationIds?: string[];
  readonly searchQuery?: string;
  readonly sort?: GridSortModel;
  readonly params?: SearchOrganizationsV1QueryParams;
  readonly skipRequest?: boolean;
}

export interface UseOrganizationDetailsTabRequestResult extends ResponseListData {
  readonly isFirstLoading: boolean;
}

export function useOrganizationDetailsTabRequest({
  organizationIds,
  searchQuery,
  sort,
  params,
  skipRequest,
}: UseOrganizationDetailsTabRequestParams): UseOrganizationDetailsTabRequestResult {
  const searchQueryFilter = useMemo(
    () =>
      searchQuery
        ? [
            {
              filterType: searchFilterFilterType.fulltext,
              name: '',
              operator: searchOperator.equals,
              value: `*${searchQuery}*`,
            },
          ]
        : [],
    [searchQuery],
  );

  const body = useMemo<SearchOrganizationsV1MutationRequest>(
    () => ({
      filters: [...getAppStoreOrgTabCommonFilters(organizationIds), ...searchQueryFilter],
      sort: transformOrganizationGridSortToRequestCall(toArray(sort)),
    }),
    [organizationIds, searchQueryFilter, sort],
  );

  // const params = useMemo<SearchOrganizationsV1QueryParams>(() => {
  //   return { offset: 0 };
  // }, []);

  const isEnabled = useMemo(() => {
    return !!organizationIds?.length && !skipRequest;
  }, [organizationIds, skipRequest]);

  const query = useMemo(() => ({ body, params, isEnabled }), [body, isEnabled, params]);

  const provisioningOrganizationQuery = useBusinessServiceRequest(
    DeviceManagementService,
    useCallback(
      (deviceManagementService, isRefetch) =>
        query.isEnabled ? deviceManagementService.getProvisioningOrganizationsList(query, isRefetch) : null,
      [query],
    ),
  );

  const {
    data: systemPackageOrganizationsList,
    isError: isSystemPackageOrganizationsListError,
    isLoading: isSystemPackageOrganizationsListLoading,
    isSuccess: isSystemPackageOrganizationsListSuccess,
    refetch,
  } = useMemo(() => provisioningOrganizationQuery, [provisioningOrganizationQuery]);

  const filteredAppsData = useMemo(
    () => getDisplayedAppStoreOrganizationTabInfo(systemPackageOrganizationsList?.data),
    [systemPackageOrganizationsList],
  );

  const isFirstLoading = useQueryFirstLoading(provisioningOrganizationQuery) && isEnabled;

  return useMemo<UseOrganizationDetailsTabRequestResult>(
    () => ({
      data: toArray(filteredAppsData),
      isError: isSystemPackageOrganizationsListError,
      isFetching: isSystemPackageOrganizationsListLoading,
      isLoading: isSystemPackageOrganizationsListLoading,
      isSuccess: isSystemPackageOrganizationsListSuccess,
      pagination: systemPackageOrganizationsList?.pagination,
      refetch,
      isFirstLoading,
    }),
    [
      filteredAppsData,
      isSystemPackageOrganizationsListError,
      isSystemPackageOrganizationsListLoading,
      isSystemPackageOrganizationsListSuccess,
      systemPackageOrganizationsList?.pagination,
      refetch,
      isFirstLoading,
    ],
  );
}
